import { Alert, Dialog, Grid, InputAdornment, Skeleton, Stack, TextField } from '@all-manager/design-system-v2';
import { Controller } from "react-hook-form";
import DateField from "../../../../../components/modals/components/DateFieldOk";
import DescriptionFieldOk from "../../../../../components/modals/components/DescriptionFieldOk";
import type { SubjectHistoryFeed } from "../../../../../types";
import DocumentsFormSection from "../../../../documents/external-components/DocumentsFormSection/DocumentsFormSection";
import ProviderAutocomplete from "../../../../providers/external-components/ProviderAutocomplete/ProviderAutocomplete";
import useAddQuoteApprovedActionModal from "./useAddQuoteApprovedActionModal";

type Props = {
  onClose: () => void;
  subjectId: string;
  item?: SubjectHistoryFeed;
  isCopy?: boolean;
}

const EURO_ADORNMENT = { input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } };

export default function AddQuoteApprovedActionModal(props: Props) {
  const { form, handleConfirm, isLoading, serverError, isDocumentLoading } = useAddQuoteApprovedActionModal(props);

  return (
    <Dialog
      open
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      title={(props.item && !props.isCopy) ? "Modifier le bon pour accord" : "Nouveau bon pour accord"}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: 'Enregistrer',
          onClick: form.handleSubmit(handleConfirm),
          loading: isLoading,
          disabled: isDocumentLoading,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isLoading,
        },
      }}
    >
      <Stack spacing={2}>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}

        <DateField
          name="date"
          form={form}
          label="Date"
          isTimeManaged={false}
          required
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Controller
              control={form.control}
              name='provider_id'
              render={({ field, fieldState }) => (
                <ProviderAutocomplete
                  label="Prestataire"
                  fullWidth
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 5 }}>
            <Controller
              control={form.control}
              name='amount'
              render={({ field, fieldState }) => (
                <TextField
                  name={field.name}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  label="Montant"
                  type="number"
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value ? parseFloat(event.target.value) : null)}
                  helperText={fieldState.error?.message}
                  error={!!fieldState.error}
                  slotProps={EURO_ADORNMENT}
                />
              )}
            />
          </Grid>
        </Grid>

        <DescriptionFieldOk
          form={form}
          name="comment"
          label="Commentaire"
        />

        {isDocumentLoading ? (
          <Skeleton variant="rounded" height={120} aria-label="Chargement des documents" />
        ) : (
          <Controller
            name='documents'
            control={form.control}
            render={({ field }) => (
              <DocumentsFormSection
                documents={field.value}
                onChange={field.onChange}
                subjectId={props.subjectId}
                defaultDocumentType="Bon pour accord"
              />
            )}
          />
        )}
      </Stack>
    </Dialog>
  );
}
